import { ReactElement, useContext, useEffect, useState } from "react";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Tab,
  Tabs,
  Tooltip
} from "@mui/material";
import TabPanel from "../../../../components/tabPanel";
import { useDispatch, useSelector } from "react-redux";
import { IContentAreaProps, IState } from "../../../../types";
import Select from "@mui/material/Select";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ConfigPanel from "./configPanel";
import { useAsyncFetch, useFetch } from "../../../../system/useFetch";
import { ICatalog, ICatalogRepository, IEditionResponse, IFamilyBase } from "../../types";
import FamilyDialog from "./familyDialog";
import { addAlert } from "../../../../actions";
import { SystemContext } from "../../../../context/systemContext";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const CatalogPage = ({ parentSize }: IContentAreaProps): ReactElement => {
  const dispatch = useDispatch<any>();
  const systemContext  = useContext(SystemContext);
  const selectedNode = useSelector((state: IState) => state.selectedNode);
  const fullAccess = systemContext.user?.permissions.find(x => x.code === `SPEC_${selectedNode.code}_FULL`.toUpperCase()) !== undefined;
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [wasEdited, setWasEdited] = useState(false);
  const [tab, setTab] = useState(0);
  const [catalogRepository, setCatalogRepository] = useState<ICatalogRepository>({
    families: [],
    selectedFamily: null,
    catalogs: null,
    selectedChildNode: null,
    selectedCatalog: null
  });
  const [editContext, setEditContext] = useState<IFamilyBase | null>(null);
  const [execCommand] = useAsyncFetch<IEditionResponse>();
  const [ getCatalogs ] = useAsyncFetch<ICatalog[]>();
  const { data } = useFetch<IFamilyBase[]>(
    {
      module: "catalog",
      query: "getFamilies",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type
      }
    });

  useEffect(() => {
    if (data) {
      setCatalogRepository( { ...catalogRepository, families: data } );
    }
  }, [data]);

  useEffect(() => {
    const childID = tab === 0 ? null : tab;

    if (catalogRepository.selectedFamily && catalogRepository.catalogs) {
      const catalog = catalogRepository.catalogs.find(x => x.familyID === catalogRepository.selectedFamily?.familyID && x.childID === childID) ?? null;

      if (!catalog) {
        const newCatalog: ICatalog = {
          nodeID: selectedNode.nodeID,
          nodeType: selectedNode.type,
          familyID: catalogRepository.selectedFamily.familyID ?? 0,
          childID,
          categories: []
        };

        execCommand({
          module: "catalog",
          query: "saveCatalog",
          payload: {
            catalog: newCatalog
          }
        }).then(response => {
          if(response && response.result) {
            newCatalog.catalogID = response.editedID;
            setCatalogRepository( { ...catalogRepository, selectedCatalog: newCatalog, catalogs: [ ...catalogRepository.catalogs ?? [], newCatalog ] });
          }
        });
      } else {
        setCatalogRepository( { ...catalogRepository, selectedCatalog: catalog });
      }
    } else {
      setCatalogRepository( { ...catalogRepository, selectedCatalog: null });
    }
  }, [tab, catalogRepository.selectedFamily, catalogRepository.catalogs]);

  const handleTabChange = (_event: any, newValue: number) => {
    setTab(newValue);
  };

  const onSelectFamily = (familyID: number) => {
    setCatalogRepository( {
      ...catalogRepository,
      selectedFamily: catalogRepository.families.find(x => x.familyID === familyID) ?? null,
      catalogs: null
    });

    getCatalogs({
      module: "catalog",
      query: "getCatalogs",
      payload: {
        nodeID: selectedNode.nodeID,
        nodeType: selectedNode.type,
        familyID
      }
    }).then(catalogs => {
      setCatalogRepository( {
        ...catalogRepository,
        selectedFamily: catalogRepository.families.find(x => x.familyID === familyID) ?? null,
        catalogs: catalogs ?? []
      });
    });
  };

  const onAddFamily = () => {
    setEditContext({
      name: "Name",
      description: "Description",
      products: [],
      nodeType: null,
      nodeID: null
    });
  };

  // const onRemoveFamily = () => {
  //   console.log("onRemoveFamily");
  // };

  const onEditFamily = () => {
    if (catalogRepository.selectedFamily) {
      setEditContext(catalogRepository.selectedFamily);
    }
  };

  const onAccept = (editedFamily: IFamilyBase) => {
    if (editContext){
      execCommand({
        module: "catalog",
        query: "saveFamily",
        payload: {
          family: editedFamily,
          nodeID: selectedNode.nodeID,
          nodeType: selectedNode.type
        }
      }).then(result => {
        if(result && result.result) {
          if(editContext.familyID){
            const families = catalogRepository.families.map(f => f.familyID === editContext.familyID ? result.content : f);
            setCatalogRepository( {
              ...catalogRepository,
              families,
              selectedFamily: families.find(x => x.familyID === editContext.familyID) ?? null,
              catalogs: null
            });
          } else {
            const families = [ ...catalogRepository.families, result.content ];
            setCatalogRepository( {
              ...catalogRepository,
              families,
              selectedFamily: families.find(x => x.familyID === result.content.familyID) ?? null,
              catalogs: null
            });
          }

          setEditContext(null);
          dispatch(addAlert({ message: "The family was saved!", severity: "success", hash: undefined }));
        } else {
          dispatch(addAlert({ message: "The family could not be saved!", severity: "error", hash: undefined }));
        }
      });
    }
  };

  const onUpdateCatalog = () => {
    setCatalogRepository( { ...catalogRepository, catalogs: [ ...catalogRepository.catalogs ?? []] });
    setWasEdited(true);
  };

  const child = selectedNode.children ? selectedNode.children.find(x => x.nodeID === tab) : undefined;
  const childCode = child ? child.code : tab === 0 ? "GENERAL" : "undefined";
  const currentPermission = `SPEC_${selectedNode.code}_${childCode}`.toUpperCase();
  const allowEdit = catalogRepository.selectedFamily !== null
    && (fullAccess || systemContext.user?.permissions.find(x => x.code.toUpperCase() === currentPermission) !== undefined);

  return (
    <>
      <div className="display-grid p-2" style={{ gridTemplateRows: "auto auto 1fr" }}>
        <div className="display-flex-row gap-1 align-items-center">
          <FormControl className="form-control" >
            <InputLabel id="demo-simple-select-label">Specification</InputLabel>
            <Select
                style={{ width: 200 }}
              variant="standard"
              value={catalogRepository.selectedFamily?.familyID ?? 0}
              displayEmpty={true}
              disabled={wasEdited}
              onChange={event => onSelectFamily(event.target.value as number)}>
              {!catalogRepository.selectedFamily && <MenuItem value={0}>UNSELECTED</MenuItem>}
              { catalogRepository.families && catalogRepository.families.map(family => {
                return <MenuItem key={`family-${family.familyID}`} value={family.familyID}>{family.name}</MenuItem>;
              })}
            </Select>
          </FormControl>

          <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }} color="success" onClick={onAddFamily} disabled={wasEdited}>
            <Tooltip title={"Add Specification"}>
              <AddIcon />
            </Tooltip>
          </IconButton>

          {/*<IconButton sx={{ padding: 0, margin: 0 }} style={{ maxWidth: 21 }} color="info" onClick={onEditFamily} disabled={wasEdited || !catalogRepository.selectedFamily || !catalogRepository.selectedFamily.familyID}>*/}
          {/*  <Tooltip title={"Export to PDF"}>*/}
          {/*    <PictureAsPdfIcon />*/}
          {/*  </Tooltip>*/}
          {/*</IconButton>*/}

          <IconButton style={{ padding: 0, margin: 0, maxWidth: 21 }} color="info" onClick={onEditFamily} disabled={wasEdited || !catalogRepository.selectedFamily || !catalogRepository.selectedFamily.familyID}>
            <Tooltip title={"Edit Specification"}>
              <EditIcon />
            </Tooltip>
          </IconButton>

          {/*<IconButton sx={{ padding: 0, margin: 0 }} style={{ maxWidth: 21 }} color="error" onClick={onRemoveFamily} disabled={!selectedFamily || !selectedFamily.familyID}>*/}
          {/*  <Tooltip title={"Remove Family"}>*/}
          {/*    <RemoveIcon />*/}
          {/*  </Tooltip>*/}
          {/*</IconButton>*/}
        </div>

        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          <Tab value={0} label={"General"} disabled={!catalogRepository.selectedFamily || wasEdited}/>
          { selectedNode.children && selectedNode.children.filter(x => !x.inactivated).map(node => {
            return <Tab key={`tab-machine-${node.nodeID}`} value={node.nodeID} label={node.name} disabled={!catalogRepository.selectedFamily || node.inactivated || wasEdited} />;
          })}
        </Tabs>

        <TabPanel value={0} index={0} style={{ alignSelf: "stretch",  display: "grid" }} >
          <ConfigPanel catalogRepository={catalogRepository}
            updateCatalog={onUpdateCatalog}
            wasEdited={wasEdited}
            setWasEdited={setWasEdited}
            allowEdit={allowEdit}
            parentSize={parentSize}/>
        </TabPanel>
      </div>

      {editContext && <FamilyDialog  family={editContext} onAccept={onAccept} onCancel={() => setEditContext(null)}/>}
    </>
  );
};

export default CatalogPage;
